import { useCallback, useEffect, useState } from 'react';
import { proposalService } from './services/proposalService';

/**
 * Whether each of a project's proposal links is on and whether it was sent
 * to the customer (with an estimate), and a way to turn one off — for the
 * Customer Proposal list in Documents. The share records are server-only, so this
 * reads them through `listProposalShares`.
 *
 * @param {string} projectId
 * @param {boolean} active - load only while the proposal list is open
 * @returns {{
 *   enabledByToken: Record<string, boolean> | null,  // null while loading
 *   sentByToken: Record<string, { sentTo: string, sentAt: number }>,  // sent ones only
 *   loadFailed: boolean,  // the statuses could not be read: each row says so
 *   revoke: (token: string) => Promise<void>,
 * }}
 */
export default function useProposalLinks(projectId, active) {
  const [enabledByToken, setEnabledByToken] = useState(null);
  const [sentByToken, setSentByToken] = useState({});
  const [loadFailed, setLoadFailed] = useState(false);

  useEffect(() => {
    if (!active || !projectId) return undefined;
    let cancelled = false;
    setLoadFailed(false);
    proposalService
      .listShares(projectId)
      .then((shares) => {
        if (cancelled) return;
        setEnabledByToken(
          Object.fromEntries(shares.map((s) => [s.token, s.enabled]))
        );
        setSentByToken(
          Object.fromEntries(
            shares
              .filter((s) => s.sentAt)
              .map((s) => [s.token, { sentAt: s.sentAt, sentTo: s.sentTo }])
          )
        );
      })
      .catch((err) => {
        console.error('[proposal links] could not load link status:', err);
        if (!cancelled) {
          setEnabledByToken({});
          setLoadFailed(true);
        }
      });
    return () => {
      cancelled = true;
    };
  }, [projectId, active]);

  const revoke = useCallback(
    async (token) => {
      await proposalService.revokeShare(projectId, token);
      setEnabledByToken((m) => ({ ...(m || {}), [token]: false }));
    },
    [projectId]
  );

  return { enabledByToken, sentByToken, loadFailed, revoke };
}
